/**
 * Upgrade Prompt Store
 *
 * Global state for the upgrade prompt modal shown when user hits
 * 402 Payment Required responses (entitlement blocked).
 *
 * WP-BILLING-4: Phase 3 - Global 402 Handler
 */
import { create } from 'zustand';

/**
 * Feature identifier from 402 response
 * Used to show context-specific upgrade messages
 *
 * WP-BILLING-2: Updated to use colon format matching backend FeatureKey.
 * Format: "category:feature" (e.g., "documents:upload", "balance:time_travel")
 */
export type BlockedFeature =
  // Write access features (requires active subscription)
  | 'documents:upload'
  | 'accounts:sync'
  | 'accounts:create'
  | 'connections:add'
  | 'balance:anchors'
  | 'balance:time_travel'
  // Flow features
  | 'flows:historical'
  // Export features
  | 'export:pdf'
  | 'export:csv'
  | string; // Allow unknown features for forward compatibility

interface UpgradePromptState {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The feature that triggered the prompt (from 402 response) */
  feature: BlockedFeature | null;
  /** Human-readable feature name for display */
  featureLabel: string | null;

  /** Show the upgrade prompt */
  show: (feature?: BlockedFeature) => void;
  /** Hide the upgrade prompt */
  hide: () => void;
}

/**
 * Map feature identifiers to human-readable labels
 *
 * WP-BILLING-2: Updated to use colon format matching backend FeatureKey.
 * Keys match the exact strings returned in 402 response "feature" field.
 */
const featureLabels: Record<string, string> = {
  // Write access features
  'documents:upload': 'Document Upload',
  'accounts:sync': 'Account Sync',
  'accounts:create': 'Add Manual Account',
  'connections:add': 'Add Bank Connection',
  'balance:anchors': 'Balance Anchors',
  'balance:time_travel': 'Time Travel',
  // Flow features
  'flows:historical': 'Flow Visualization',
  // Export features
  'export:pdf': 'PDF Export',
  'export:csv': 'CSV Export',
};

/**
 * Get human-readable label for a feature
 *
 * WP-BILLING-2: Handles colon-separated feature keys (e.g., "documents:upload")
 * by converting them to Title Case if no explicit label is defined.
 */
function getFeatureLabel(feature: BlockedFeature | null): string | null {
  if (!feature) return null;

  // Check for explicit label first
  if (featureLabels[feature]) {
    return featureLabels[feature];
  }

  // Handle colon-separated keys: "category:feature" -> "Category Feature"
  if (feature.includes(':')) {
    return feature
      .split(':')
      .map((part) => part.replace(/_/g, ' '))
      .map((part) => part.replace(/\b\w/g, (c) => c.toUpperCase()))
      .join(' ');
  }

  // Fallback for legacy underscore format
  return feature.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Upgrade prompt store
 * Manages global state for the 402 upgrade prompt modal
 */
export const useUpgradePromptStore = create<UpgradePromptState>((set) => ({
  isOpen: false,
  feature: null,
  featureLabel: null,

  /**
   * Show the upgrade prompt
   * @param feature - Optional feature identifier from 402 response
   */
  show: (feature?: BlockedFeature) =>
    set({
      isOpen: true,
      feature: feature ?? null,
      featureLabel: getFeatureLabel(feature ?? null),
    }),

  /**
   * Hide the upgrade prompt
   */
  hide: () =>
    set({
      isOpen: false,
      feature: null,
      featureLabel: null,
    }),
}));

export default useUpgradePromptStore;
